/* eslint-disable jsx-a11y/label-has-associated-control */
import axios from 'axios'
import { ChevronDown, CircleAlert } from 'lucide-react'
import React, { useCallback, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { HiOutlineLocationMarker, HiOutlineUser } from 'react-icons/hi'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import { BRANDS_WITH_REQUIRED_FREIGHT } from '../../../../../utils/constants'
import {
  getCartSplitDeliveryEstimateTexts,
  orderHasPillItem,
  PILL_DELIVERY_CEP_TEXT
} from '../../../../../utils/deliveryEstimate'

interface AddressStepProps {
  onNextStep: () => void
  onPreviousStep: () => void
  onAddressDataChange: (data: any) => void
  initialAddressData?: any
}

interface AddressForm {
  fullName: string
  cpf: string
  phone: string
  email: string

  street: string
  number: string
  complement: string
  neighborhood: string
  city: string
  state: string
  cep: string
  birth?: string
  gender?: string
  civil_status?: string
  is_discrete_shipping?: boolean
}

export const AddressStep: React.FC<AddressStepProps> = ({
  onNextStep: _onNextStep,
  onPreviousStep: _onPreviousStep,
  onAddressDataChange,
  initialAddressData
}) => {
  const { lastOrderCreated, lastOrderPaid, getLastOrderPaid, deliveryCep, setDeliveryCep, validatingDeliveryCep } =
    usePrepareCart()
  const items = lastOrderCreated?.items || []
  const shippingRestriction =
    lastOrderCreated?.shipping_restriction ?? lastOrderCreated?.summary?.shipping_restriction ?? null
  const formatEstimatedRangeText = (start?: string | null, end?: string | null) => {
    if (!start || !end) return null

    const [startYear, startMonth, startDay] = start.split('-')
    const [endYear, endMonth, endDay] = end.split('-')

    if (!startYear || !startMonth || !startDay || !endYear || !endMonth || !endDay) return null

    return `A entrega estimada é entre ${startDay}/${startMonth}/${startYear} e ${endDay}/${endMonth}/${endYear}.`
  }

  const {
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors }
  } = useForm<AddressForm>({
    mode: 'onChange',
    defaultValues: {
      fullName: '',
      cpf: '',
      phone: '',
      email: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      cep: deliveryCep || '',
      civil_status: '',
      is_discrete_shipping: false
    }
  })

  const didInitializeRef = useRef<boolean>(false)

  useEffect(() => {
    if (didInitializeRef.current) return
    if (initialAddressData && (initialAddressData?.street || initialAddressData?.cep)) {
      reset({
        fullName: initialAddressData.fullName || '',
        cpf: initialAddressData.cpf || '',
        phone: initialAddressData.phone || '',
        email: initialAddressData.email || '',
        birth: initialAddressData.birth || '',
        gender: initialAddressData.gender || '',
        street: initialAddressData.street || '',
        number: initialAddressData.number || '',
        complement: initialAddressData.complement || '',
        neighborhood: initialAddressData.neighborhood || '',
        city: initialAddressData.city || '',
        state: initialAddressData.state || '',
        cep: initialAddressData.cep || deliveryCep || '',
        civil_status: initialAddressData.civil_status || '',
        is_discrete_shipping: initialAddressData.is_discrete_shipping ?? false
      })
      setDeliveryCep(initialAddressData.cep || deliveryCep || '')
      onAddressDataChange(initialAddressData)
      didInitializeRef.current = true
    }
  }, [initialAddressData, reset, onAddressDataChange, deliveryCep, setDeliveryCep])

  const fetchZipCode = useCallback(
    async (cep: string) => {
      try {
        const { data } = await axios.get(`https://viacep.com.br/ws/${cep}/json/`)

        if (data?.erro) {
          setError('cep', { type: 'manual', message: 'CEP inválido.' })
          return
        }

        clearErrors('cep')
        setValue('street', data.logradouro || '')
        setValue('neighborhood', data.bairro || '')
        setValue('city', data.localidade || '')
        setValue('state', data.uf || '')
      } catch (error) {
        console.error('Erro ao buscar CEP:', error)
        setError('cep', { type: 'manual', message: 'CEP inválido.' })
      }
    },
    [setValue, setError, clearErrors]
  )

  useEffect(() => {
    if (didInitializeRef.current) return
    if (lastOrderCreated?.person) {
      const { person } = lastOrderCreated

      let formattedBirthday = ''
      if (person.birthday) {
        if (person.birthday.match(/^\d{4}-\d{2}-\d{2}$/)) {
          formattedBirthday = person.birthday
        } else {
          const date = new Date(person.birthday)
          if (!Number.isNaN(date.getTime())) {
            const [isoDate] = date.toISOString().split('T')
            formattedBirthday = isoDate
          }
        }
      }

      let formattedGender = ''
      if (person.gender) {
        const gender = person.gender.toUpperCase()
        if (['M', 'F', 'N'].includes(gender)) {
          formattedGender = gender
        }
      }

      const values: Partial<AddressForm> = {
        fullName: person.name || person.full_name || '',
        cpf: person.cpf || '',
        phone: person.phone || person.mobile || '',
        email: person.email || '',
        birth: formattedBirthday,
        gender: formattedGender,
        street: person.personal_address || lastOrderPaid?.street || '',
        number: person.personal_address_number || lastOrderPaid?.address_number || '',
        complement: person.address_complement || lastOrderPaid?.complement || '',
        neighborhood: person.personal_address_district || lastOrderPaid?.district || '',
        city: person.personal_address_city || lastOrderPaid?.city || '',
        state: person.personal_address_state || lastOrderPaid?.state || '',
        cep: deliveryCep || person.personal_cep || lastOrderPaid?.cep || '',
        civil_status: person.civil_status || '',
        is_discrete_shipping: initialAddressData?.is_discrete_shipping ?? false
      }
      reset({
        fullName: values.fullName || '',
        cpf: values.cpf || '',
        phone: values.phone || '',
        email: values.email || '',
        birth: values.birth || '',
        gender: values.gender || '',
        street: values.street || '',
        number: values.number || '',
        complement: values.complement || '',
        neighborhood: values.neighborhood || '',
        city: values.city || '',
        state: values.state || '',
        cep: values.cep || '',
        civil_status: values.civil_status || '',
        is_discrete_shipping: values.is_discrete_shipping ?? false
      })
      setDeliveryCep(values.cep || '')
      onAddressDataChange(values)
      didInitializeRef.current = true
    }
  }, [lastOrderCreated, reset, onAddressDataChange, lastOrderPaid, initialAddressData, deliveryCep, setDeliveryCep])

  useEffect(() => {
    getLastOrderPaid(lastOrderCreated?.person?.id)
  }, [lastOrderCreated])

  const lastSentRef = useRef<string>('')
  useEffect(() => {
    const subscription = watch((value) => {
      try {
        const str = JSON.stringify(value)
        if (str !== lastSentRef.current) {
          lastSentRef.current = str
          onAddressDataChange(value)
        }
      } catch (e) {
        // ignore serialization errors
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, onAddressDataChange])

  const cepValue = watch('cep')
  const isDiscreteShippingValue = watch('is_discrete_shipping')
  const estimatedRangeText = formatEstimatedRangeText(
    lastOrderCreated?.estimated_delivery_range_start,
    lastOrderCreated?.estimated_delivery_range_end
  )
  const splitEstimateTexts = getCartSplitDeliveryEstimateTexts(lastOrderCreated)
  const hasPillItem = orderHasPillItem(lastOrderCreated)
  const containsProductWithLongerDeliveryTime = items.some(
    (item: any) => item?.product?.stock === 0 && item?.product?.class_id !== 6
  )
  const containProductsMatirz = items.some((item: any) => {
    const hubName = item?.product?.brand?.distributor?.hubName || ''
    const normalizedHubName = hubName.trim().toUpperCase()
    return normalizedHubName !== 'HUB USA' && normalizedHubName !== 'HUB_USA' && normalizedHubName !== 'HUB EUA'
  })
  const containsProductWithHubDistributor = items.some((item: any) => {
    const hubName = item?.product?.brand?.distributor?.hubName || ''
    const normalizedHubName = hubName.trim().toUpperCase()
    return normalizedHubName === 'HUB USA' || normalizedHubName === 'HUB_USA' || normalizedHubName === 'HUB EUA'
  })
  const isAllSupplements = items.every(
    (item: any) =>
      item?.product?.class_id === 6 && !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )
  const isAllPillItems = items.every((item: any) => item?.product?.origin === 'pill')

  let deliveryWarningText = estimatedRangeText

  if (!deliveryWarningText && hasPillItem) {
    deliveryWarningText = PILL_DELIVERY_CEP_TEXT
  } else if (!deliveryWarningText && containsProductWithLongerDeliveryTime) {
    deliveryWarningText = 'Existem produtos com possível maior tempo de entrega.'
  } else if (!deliveryWarningText && containProductsMatirz) {
    deliveryWarningText = 'O prazo de entrega é de 25 dias úteis a partir da confirmação do pagamento.'
  } else if (!deliveryWarningText && containsProductWithHubDistributor) {
    deliveryWarningText = 'O prazo de entrega é de 15 dias úteis a partir da confirmação do pagamento.'
  }
  useEffect(() => {
    const digits = (cepValue || '').replace(/\D/g, '')
    if (digits.length === 8) {
      fetchZipCode(digits).catch(() => {})
    }
  }, [cepValue, fetchZipCode])

  useEffect(() => {
    if ((cepValue || '') !== deliveryCep) {
      setValue('cep', deliveryCep, { shouldValidate: true, shouldDirty: true })
    }
  }, [deliveryCep, cepValue, setValue])

  return (
    <div className="h-fit w-full space-y-8">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        {!isAllPillItems && (
          <div className="mb-6 flex h-16 w-full items-center gap-4 rounded border border-yellow-200 bg-[#FFEEC299] px-4 py-2.5 sm:h-12">
            <div className="flex h-4 w-4 items-center justify-center rounded-full">
              <CircleAlert className="h-4 w-4 text-neutral-500" />
            </div>
            <p className="text-[11px] font-normal text-[#898483]">
              <span className="font-bold">IMPORTANTE:</span> Garanta que o endereço corresponda ao que está no
              comprovante de residência.
            </p>
          </div>
        )}
        {!isAllSupplements &&
          (validatingDeliveryCep || deliveryWarningText) &&
          shippingRestriction?.covered !== false && (
            <div className="mb-6 flex h-auto min-h-16 w-full items-center gap-4 rounded border border-yellow-200 bg-[#FFEEC299] px-4 py-2.5 sm:min-h-12">
              <div className="flex h-4 w-4 items-center justify-center rounded-full">
                <CircleAlert className="h-4 w-4 text-neutral-500" />
              </div>
              {validatingDeliveryCep ? (
                <div className="flex items-center gap-2">
                  <span
                    className="animate-spin"
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      border: '2px solid rgba(0,0,0,0.15)',
                      borderLeftColor: '#e6a817',
                      borderRadius: '50%',
                      flexShrink: 0
                    }}
                  />
                  <p className="text-[11px] font-normal text-[#898483]">
                    <span className="font-bold">IMPORTANTE:</span> Calculando estimativa de entrega...
                  </p>
                </div>
              ) : splitEstimateTexts ? (
                <div className="flex flex-col gap-1">
                  {splitEstimateTexts.map((text, index) => (
                    <p key={text} className="text-[11px] font-normal text-[#898483]">
                      {index === 0 ? (
                        <>
                          <span className="font-bold">IMPORTANTE:</span> {text}
                        </>
                      ) : (
                        text
                      )}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] font-normal text-[#898483]">
                  <span className="font-bold">IMPORTANTE:</span> {deliveryWarningText}
                </p>
              )}
            </div>
          )}
        {shippingRestriction?.covered === false && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            <p className="mb-1 font-semibold">Infelizmente não conseguimos entregar este produto na sua região.</p>
            <p className="mb-1">Os itens abaixo exigem transporte especial que não atende ao CEP informado.</p>
            {shippingRestriction.blocked_products?.length > 0 && (
              <ul className="ml-3 list-disc">
                {shippingRestriction.blocked_products.map((p: { product_id: number; name: string }) => (
                  <li key={p.product_id}>{p.name}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {shippingRestriction?.requires_cep && !deliveryCep && (
          <p className="mb-2 text-xs text-amber-700">
            Informe o CEP para verificar a disponibilidade de transporte antes de avançar.
          </p>
        )}
        <div className="mb-2 flex items-center gap-1">
          <HiOutlineLocationMarker className="h-4 w-4 text-primary-700" />
          <h2 className="text-sm font-semibold text-primary-700">Endereço de entrega</h2>
        </div>
        <p className="mb-6 text-sm text-neutral-800">Preencha os dados de entrega, por favor</p>

        {lastOrderCreated?.deliveryByAssociation && (
          <div className="mb-4 flex items-center gap-2">
            <input
              type="checkbox"
              id="is_discrete_shipping"
              {...register('is_discrete_shipping')}
              checked={!!isDiscreteShippingValue}
              onChange={(e) =>
                setValue('is_discrete_shipping', e.target.checked, {
                  shouldValidate: true,
                  shouldDirty: true
                })
              }
              className="h-4 w-4 accent-primary-700"
            />
            <label htmlFor="is_discrete_shipping" className="text-sm text-neutral-700">
              Entrega discreta (sem identificação da associação)
            </label>
          </div>
        )}
        <div className="flex flex-col space-y-5">
          {/* CEP primeiro */}
          <div className="flex w-full gap-5">
            <div className="flex flex-1 flex-col">
              <label htmlFor="cep" className="mb-1 text-sm text-neutral-500">
                CEP<span className="text-red-500">*</span>
              </label>
              <input
                {...register('cep', {
                  required: 'CEP é obrigatório',
                  pattern: { value: /^\d{5}-?\d{3}$/, message: 'CEP inválido' },
                  onChange: (e) => {
                    const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 8)
                    const masked = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits
                    e.target.value = masked
                    setValue('cep', masked, { shouldValidate: true, shouldDirty: true })
                    setDeliveryCep(masked)
                  }
                })}
                name="cep"
                id="cep"
                placeholder="ex: 12345-678"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.cep && (
                <span className="mt-1 text-xs text-red-600">{String(errors.cep.message || 'CEP inválido')}</span>
              )}
            </div>
          </div>

          {/* Endereço + Número + Complemento */}
          <div className="flex w-full gap-5">
            <div className="flex flex-[3] flex-col">
              <label htmlFor="street" className="mb-1 text-sm text-neutral-500">
                Endereço<span className="text-red-500">*</span>
              </label>
              <input
                {...register('street', { required: 'Endereço é obrigatório' })}
                name="street"
                id="street"
                placeholder="ex:Avenida das Flores, 456"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.street && (
                <span className="mt-1 text-xs text-red-600">
                  {String(errors.street.message || 'Campo obrigatório')}
                </span>
              )}
            </div>
            <div className="flex flex-[1] flex-col">
              <label htmlFor="number" className="mb-1 text-sm text-neutral-500">
                Número<span className="text-red-500">*</span>
              </label>
              <input
                {...register('number', {
                  required: 'Número é obrigatório',
                  pattern: { value: /^\d+$/, message: 'Apenas números' },
                  onChange: (e) => {
                    const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 8)
                    e.target.value = digits
                    setValue('number', digits, { shouldValidate: true, shouldDirty: true })
                  }
                })}
                name="number"
                id="number"
                placeholder="ex:123"
                inputMode="numeric"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.number && (
                <span className="mt-1 text-xs text-red-600">
                  {String(errors.number.message || 'Campo obrigatório')}
                </span>
              )}
            </div>
            <div className="flex flex-[1] flex-col">
              <label htmlFor="complement" className="mb-1 text-sm text-neutral-500">
                Complemento
              </label>
              <input
                {...register('complement')}
                name="complement"
                id="complement"
                placeholder="ex:Apto 156"
                maxLength={64}
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
            </div>
          </div>

          {/* Bairro */}
          <div className="flex w-full gap-5">
            <div className="flex flex-1 flex-col">
              <label htmlFor="neighborhood" className="mb-1 text-sm text-neutral-500">
                Bairro<span className="text-red-500">*</span>
              </label>
              <input
                {...register('neighborhood', { required: 'Bairro é obrigatório' })}
                name="neighborhood"
                id="neighborhood"
                placeholder="ex: Bela Vista"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.neighborhood && (
                <span className="mt-1 text-xs text-red-600">
                  {String(errors.neighborhood.message || 'Campo obrigatório')}
                </span>
              )}
            </div>
          </div>

          {/* Cidade + Estado */}
          <div className="flex w-full gap-5">
            <div className="flex flex-1 flex-col">
              <label htmlFor="city" className="mb-1 text-sm text-neutral-500">
                Cidade<span className="text-red-500">*</span>
              </label>
              <input
                {...register('city', { required: 'Cidade é obrigatória' })}
                name="city"
                id="city"
                placeholder="ex:São Paulo"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.city && (
                <span className="mt-1 text-xs text-red-600">{String(errors.city.message || 'Campo obrigatório')}</span>
              )}
            </div>
            <div className="flex flex-1 flex-col">
              <label htmlFor="state" className="mb-1 text-sm text-neutral-500">
                Estado<span className="text-red-500">*</span>
              </label>
              <input
                {...register('state', {
                  required: 'UF é obrigatória',
                  pattern: { value: /^[A-Za-z]{2}$/, message: 'UF deve ter 2 letras' }
                })}
                name="state"
                id="state"
                placeholder="ex:SP"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.state && (
                <span className="mt-1 text-xs text-red-600">{String(errors.state.message || 'Campo obrigatório')}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dados Pessoais */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-2 flex items-center gap-1">
          <HiOutlineUser className="h-4 w-4 text-primary-700" />
          <h2 className="text-sm font-semibold text-primary-700">Dados pessoais</h2>
        </div>
        <p className="mb-6 text-sm text-neutral-800">Preencha os dados pessoais, por favor</p>

        <div className="flex flex-col space-y-5">
          {/* Nome + CPF */}
          <div className="flex w-full gap-5">
            <div className="flex flex-1 flex-col">
              <label htmlFor="fullName" className="mb-1 text-sm text-neutral-500">
                Nome<span className="text-red-500">*</span>
              </label>
              <input
                {...register('fullName', { required: 'Nome é obrigatório' })}
                name="fullName"
                id="fullName"
                placeholder="Lia Venturo"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.fullName && (
                <span className="mt-1 text-xs text-red-600">
                  {String(errors.fullName.message || 'Campo obrigatório')}
                </span>
              )}
            </div>
            <div className="flex flex-1 flex-col">
              <label htmlFor="cpf" className="mb-1 text-sm text-neutral-500">
                CPF<span className="text-red-500">*</span>
              </label>
              <input
                {...register('cpf', {
                  required: 'CPF é obrigatório',
                  pattern: { value: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, message: 'CPF inválido' },
                  onChange: (e) => {
                    const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 11)
                    let masked = digits
                    if (digits.length > 3 && digits.length <= 6) masked = `${digits.slice(0, 3)}.${digits.slice(3)}`
                    if (digits.length > 6 && digits.length <= 9)
                      masked = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`
                    if (digits.length > 9)
                      masked = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`
                    e.target.value = masked
                    setValue('cpf', masked, { shouldValidate: true, shouldDirty: true })
                  }
                })}
                name="cpf"
                id="cpf"
                placeholder="000.000.000-00"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.cpf && (
                <span className="mt-1 text-xs text-red-600">{String(errors.cpf.message || 'Campo obrigatório')}</span>
              )}
            </div>
          </div>

          {/* Email + Data */}
          <div className="flex w-full gap-5">
            <div className="flex flex-1 flex-col">
              <label htmlFor="email" className="mb-1 text-sm text-neutral-500">
                E-mail<span className="text-red-500">*</span>
              </label>
              <input
                {...register('email', {
                  required: 'E-mail é obrigatório',
                  pattern: { value: /\S+@\S+\.\S+/, message: 'E-mail inválido' }
                })}
                name="email"
                id="email"
                placeholder="lia@cannect.com.br"
                type="email"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.email && (
                <span className="mt-1 text-xs text-red-600">{String(errors.email.message || 'Campo obrigatório')}</span>
              )}
            </div>
            <div className="flex flex-1 flex-col">
              <label htmlFor="birth" className="mb-1 text-sm text-neutral-500">
                Data de Nascimento<span className="text-red-500">*</span>
              </label>
              <input
                {...register('birth', { required: 'Data de nascimento é obrigatória' })}
                name="birth"
                id="birth"
                placeholder="23/06/1990"
                type="date"
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.birth && (
                <span className="mt-1 text-xs text-red-600">{String(errors.birth.message || 'Campo obrigatório')}</span>
              )}
            </div>
          </div>

          {/* Telefone + Gênero */}
          <div className="flex w-full gap-5">
            <div className="flex flex-1 flex-col">
              <label htmlFor="phone" className="mb-1 text-sm text-neutral-500">
                Celular<span className="text-red-500">*</span>
              </label>
              <input
                {...register('phone', {
                  required: 'Celular é obrigatório',
                  pattern: { value: /^\d{11}$/, message: 'Celular deve ter 11 dígitos (DDD + 9 dígitos)' },
                  onChange: (e) => {
                    const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 11)
                    e.target.value = digits
                    setValue('phone', digits, { shouldValidate: true, shouldDirty: true })
                  }
                })}
                name="phone"
                id="phone"
                placeholder="11912345678"
                inputMode="numeric"
                maxLength={11}
                style={{
                  border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                  background: 'var(--Secondary-Secondary100, #EDECE7)'
                }}
                className="h-9 w-full rounded-md px-4 py-2 text-sm text-gray-800"
              />
              {errors?.phone && (
                <span className="mt-1 text-xs text-red-600">{String(errors.phone.message || 'Campo obrigatório')}</span>
              )}
              <span className="mt-1 text-xs text-neutral-500">Formato: DDD + 9 dígitos (ex: 11987654321)</span>
            </div>
            <div className="flex flex-1 flex-col">
              <label htmlFor="gender" className="mb-1 text-sm text-neutral-500">
                Gênero/Sexo<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  {...register('gender', { required: 'Gênero é obrigatório' })}
                  name="gender"
                  id="gender"
                  style={{
                    border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                    background: 'var(--Secondary-Secondary100, #EDECE7)',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                  className="h-9 w-full rounded-md pl-3 pr-10 text-sm text-gray-800">
                  <option value="" disabled>
                    Selecione
                  </option>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                  <option value="N">Prefiro não informar</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              </div>
              {errors?.gender && (
                <span className="mt-1 text-xs text-red-600">
                  {String(errors.gender.message || 'Campo obrigatório')}
                </span>
              )}
            </div>
          </div>

          {/* Estado Civil - apenas se deliveryByAssociation for true */}
          {lastOrderCreated?.deliveryByAssociation && (
            <div className="flex w-full gap-5">
              <div className="flex flex-1 flex-col">
                <label htmlFor="civil_status" className="mb-1 text-sm text-neutral-500">
                  Estado Civil<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    {...register('civil_status', { required: 'Estado civil é obrigatório' })}
                    name="civil_status"
                    id="civil_status"
                    style={{
                      border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)',
                      background: 'var(--Secondary-Secondary100, #EDECE7)',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }}
                    className="h-9 w-full rounded-md pl-3 pr-10 text-sm text-gray-800">
                    <option value="" disabled>
                      Selecione
                    </option>
                    <option value="SOLTEIRO">Solteiro</option>
                    <option value="CASADO">Casado</option>
                    <option value="DIVORCIADO">Divorciado</option>
                    <option value="VIUVO">Viúvo</option>
                    <option value="UNIAO_ESTAVEL">União Estável</option>
                    <option value="SEPARADO">Separado</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                </div>
                {errors?.civil_status && (
                  <span className="mt-1 text-xs text-red-600">
                    {String(errors.civil_status.message || 'Campo obrigatório')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
